import { type StatsigLayerName, getStatsigClientInstance, useLayer } from '@coursera-web/statsig';

import prices from 'js/lib/prices';
import requestCountry from 'js/lib/requestCountry';

import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import paymentsBackendEpicClient from 'bundles/epic/clients/payments-backend';

/**
 * Local-currency experiment logic (which currency a cart is charged in for a given country).
 *
 * The currency a country resolves to must be identical wherever it is computed — cart creation
 * (`getExperimentCurrencyCode`) and eligibility surfaces (`useExperimentCurrencyCode`). Both read
 * the same two Statsig layers and run the same decision (`resolveCurrencyCodeFromLayers`); they
 * only differ in HOW they read the layers: cart creation runs in a user-action (CSR) path and
 * reads imperatively via `getStatsigClientInstance()`, while eligibility surfaces run at render
 * time and read via the `useLayer` hook.
 */

const BATCH_THREE_LAYER: StatsigLayerName = 'payment-platform-local-currency-batch-three';
const DLOCAL_BATCH_TWO_LAYER: StatsigLayerName = 'payment-platform-dlocal-batch-two-layer';

// Minimal shape shared by the imperative SDK layer and the typed `useLayer` result. Declared with
// method syntax (not an arrow property) so parameter bivariance lets the typed layer — whose `get`
// only accepts that layer's literal param names — satisfy this looser signature.
type ReadableLayer = { get(param: string, fallback: unknown): unknown };

const getIsBrlPixEligible = (requestCountryCode: string, isNonSubscriptionCart: boolean): boolean =>
  requestCountryCode === 'BR' &&
  ((isNonSubscriptionCart && paymentsBackendEpicClient.preview('enablePIX')) ||
    (!isNonSubscriptionCart && GoogleCertificateEpicClient.preview('enableStripeBillingEngineDlocalAB')));

/**
 * Resolves the currency code from the two already-read batch layers. Shared by both callers so the
 * decision can never diverge. Defaults to the country's currency, then each batch (in precedence
 * order — batch three wins) overrides it when enabled, the product is supported, and the country
 * is mapped; finally the BR/PIX experiment overrides everything.
 */
export const resolveCurrencyCodeFromLayers = (
  batchThreeLayer: ReadableLayer,
  dlocalBatchTwoLayer: ReadableLayer,
  requestCountryCode: string,
  productType: string,
  isNonSubscriptionCart: boolean
): string => {
  let currencyCode = prices.getCurrencyFromCountry(requestCountryCode);

  // DLocal payment method batch two experiment
  const dlocalBatchTwoEnabled = dlocalBatchTwoLayer.get('local-currency-enabled', false) as boolean;
  const dlocalBatchTwoSupportedProductTypes = dlocalBatchTwoLayer.get('supported-product-types', []) as string[];
  const dlocalBatchTwoMapping = dlocalBatchTwoLayer.get('country-currency-mapping', {}) as Record<string, string>;
  if (
    dlocalBatchTwoEnabled &&
    dlocalBatchTwoSupportedProductTypes.includes(productType) &&
    dlocalBatchTwoMapping[requestCountryCode]
  ) {
    currencyCode = dlocalBatchTwoMapping[requestCountryCode];
  }

  // Local currency batch three experiment
  const batchThreeEnabled = batchThreeLayer.get('local-currency-enabled', false) as boolean;
  const batchThreeSupportedProductTypes = batchThreeLayer.get('supported-product-types', []) as string[];
  const batchThreeMapping = batchThreeLayer.get('country-currency-mapping-2', {}) as Record<string, string>;
  if (
    batchThreeEnabled &&
    batchThreeSupportedProductTypes.includes(productType) &&
    batchThreeMapping[requestCountryCode]
  ) {
    currencyCode = batchThreeMapping[requestCountryCode];
  }

  // PIX experiment: Brazilian carts are charged in BRL, overriding the local-currency batches.
  return getIsBrlPixEligible(requestCountryCode, isNonSubscriptionCart) ? 'BRL' : currencyCode;
};

/**
 * Imperative resolver for CSR user-action paths (e.g. cart creation), reading the layers directly.
 */
export const getExperimentCurrencyCode = ({
  requestCountryCode,
  productType,
  isNonSubscriptionCart,
}: {
  requestCountryCode: string;
  productType: string;
  isNonSubscriptionCart: boolean;
}): string =>
  resolveCurrencyCodeFromLayers(
    getStatsigClientInstance().getLayer(BATCH_THREE_LAYER),
    getStatsigClientInstance().getLayer(DLOCAL_BATCH_TWO_LAYER),
    requestCountryCode,
    productType,
    isNonSubscriptionCart
  );

/**
 * Render-time resolver for eligibility surfaces, reading the layers via `useLayer`. Must follow the
 * Rules of Hooks: call unconditionally, above any early returns.
 */
export const useExperimentCurrencyCode = ({
  productType,
  isNonSubscriptionCart,
}: {
  productType: string;
  isNonSubscriptionCart: boolean;
}): string =>
  resolveCurrencyCodeFromLayers(
    useLayer(BATCH_THREE_LAYER),
    useLayer(DLOCAL_BATCH_TWO_LAYER),
    requestCountry.get(),
    productType,
    isNonSubscriptionCart
  );
